import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  Ban,
  Building2,
  Calendar,
  CalendarClock,
  CheckCircle2,
  CirclePlus,
  ExternalLink,
  Info,
  Mail,
  MapPin,
  Package,
  PenLine,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Truck,
  Upload,
} from "lucide-react";

import { DetailRow, DetailRows } from "@/components/shared/DetailRows";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  markRequestPaymentPaid,
  recheckRequestPayment,
  refundRequestPayment,
} from "@/services/platformRequestPaymentsService";
import { pauseRequest, unpauseRequest } from "@/services/platformRequestPauseService";
import {
  reassignRequest,
  type ReassignMode,
} from "@/services/platformRequestAssignmentService";
import { UserDetailDialog } from "@/components/shared/UserDetailDialog";
import { listNotaries } from "@/services/notariesService";
import { listAccountants } from "@/services/accountantsService";
import { formatUyMinor } from "@/lib/formatUyMinor";
import { AuthenticatedBlobDocumentLink } from "@/components/shared/AuthenticatedBlobDocumentLink";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { RequestDetailLayout } from "@/components/shared/RequestDetailLayout";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { serverMessage } from "@/lib/apiError";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  fetchPlatformNotarialRequestDocumentBlob,
  getPlatformNotarialRequestDetail,
  platformDeleteNotarialRequest,
  type PlatformNotarialRequestEventDto,
} from "@/services/platformRequestsService";
import type { NotarialRequestEventType } from "@/services/notaryRequestsService";
import type { NotarialRequestStatus } from "@/services/notaryRequestsService";

const EVENT_ICONS: Record<NotarialRequestEventType, JSX.Element> = {
  created: <CirclePlus className="h-4 w-4" />,
  accepted: <CheckCircle2 className="h-4 w-4" />,
  info_requested: <Info className="h-4 w-4" />,
  resubmitted: <RefreshCw className="h-4 w-4" />,
  certificate_uploaded: <ShieldCheck className="h-4 w-4" />,
  certificate_available: <ShieldCheck className="h-4 w-4" />,
  certificate_replaced: <RefreshCw className="h-4 w-4" />,
  correction_requested: <AlertTriangle className="h-4 w-4" />,
  cannot_complete: <Ban className="h-4 w-4" />,
  physical_document_ready: <Package className="h-4 w-4" />,
  physical_pickup_details_updated: <PenLine className="h-4 w-4" />,
  document_ready_for_delivery: <Package className="h-4 w-4" />,
  platform_received_document: <Building2 className="h-4 w-4" />,
  consumer_delivery_requested: <Mail className="h-4 w-4" />,
  consumer_delivery_details_submitted: <MapPin className="h-4 w-4" />,
  consumer_delivery_details_updated: <PenLine className="h-4 w-4" />,
  shipped_to_consumer: <Truck className="h-4 w-4" />,
  shipment_in_transit: <Truck className="h-4 w-4" />,
  delivered_to_consumer: <CheckCircle2 className="h-4 w-4" />,
  estimated_date_set: <Calendar className="h-4 w-4" />,
  estimated_date_changed: <CalendarClock className="h-4 w-4" />,
  document_uploaded: <Upload className="h-4 w-4" />,
  document_deleted: <Trash2 className="h-4 w-4" />,
};

const EVENT_ICON_COLORS: Record<NotarialRequestEventType, string> = {
  created: "bg-muted text-muted-foreground",
  accepted: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  info_requested: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  resubmitted: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  certificate_uploaded: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  certificate_available: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  certificate_replaced: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  correction_requested: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  cannot_complete: "bg-muted text-muted-foreground dark:bg-muted/80",
  physical_document_ready: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  physical_pickup_details_updated:
    "bg-violet-100 text-violet-900 dark:bg-violet-900/45 dark:text-violet-200",
  document_ready_for_delivery:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  platform_received_document:
    "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300",
  consumer_delivery_requested:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  consumer_delivery_details_submitted:
    "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  consumer_delivery_details_updated:
    "bg-teal-100 text-teal-900 dark:bg-teal-900/45 dark:text-teal-200",
  shipped_to_consumer:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  shipment_in_transit:
    "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  delivered_to_consumer:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  estimated_date_set:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  estimated_date_changed:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  document_uploaded:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  document_deleted:
    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const STATUS_LABELS: Record<NotarialRequestStatus, string> = {
  pending_profile_completion: "notary.requests.statusPendingProfileCompletion",
  pending_payment_verification: "requests.user.statusPendingPaymentVerification",
  pending: "notary.requests.statusPending",
  in_progress: "notary.requests.statusInProgress",
  completed: "notary.requests.statusCompleted",
  missing_information: "notary.requests.statusMissingInfo",
  cannot_complete: "notary.requests.statusCannotComplete",
  document_ready_at_professional: "notary.requests.statusDocumentReadyAtProfessional",
  document_at_platform: "notary.requests.statusDocumentAtPlatform",
  awaiting_consumer_delivery_details: "notary.requests.statusAwaitingConsumerDelivery",
  awaiting_shipment_to_consumer: "notary.requests.statusAwaitingShipment",
  in_transit_to_consumer: "notary.requests.statusInTransitToConsumer",
};

const STATUS_VARIANTS: Record<NotarialRequestStatus, StatusBadgeVariant> = {
  pending_profile_completion: "warning",
  pending_payment_verification: "warning",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  missing_information: "danger",
  cannot_complete: "softDanger",
  document_ready_at_professional: "warning",
  document_at_platform: "info",
  awaiting_consumer_delivery_details: "info",
  awaiting_shipment_to_consumer: "info",
  in_transit_to_consumer: "info",
};

function TimelineEventReadOnly({
  event,
  isLast,
}: {
  event: PlatformNotarialRequestEventDto;
  isLast: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const eventType = event.eventType as NotarialRequestEventType;
  const icon =
    EVENT_ICONS[eventType as NotarialRequestEventType] ?? <CirclePlus className="h-4 w-4" />;
  const iconColor =
    EVENT_ICON_COLORS[eventType as NotarialRequestEventType] ?? "bg-muted text-muted-foreground";
  const roleSuffix =
    event.actorRole != null
      ? t(`notary.requestDetail.actorRole_${event.actorRole}`, {
          defaultValue: event.actorRole,
        })
      : null;
  const actorLine =
    roleSuffix && event.actorDisplayName
      ? `${roleSuffix} · ${event.actorDisplayName}`
      : event.actorDisplayName ?? roleSuffix;

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${iconColor}`}
        >
          {icon}
        </div>
        {!isLast && (
          <div className="mt-1 flex-1 border-l border-dashed border-border" />
        )}
      </div>
      <div className={`min-w-0 flex-1 space-y-1 ${isLast ? "" : "pb-5"}`}>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium text-foreground">
            {t(`notary.requestDetail.event_${eventType}`, {
              defaultValue: event.eventType,
            })}
          </span>
          <span className="text-xs text-muted-foreground">
            {new Date(event.createdAt).toLocaleString()}
          </span>
        </div>
        {actorLine != null && actorLine !== "" ? (
          <p className="text-xs text-muted-foreground">{actorLine}</p>
        ) : null}
        {event.message != null && event.message.trim() !== "" ? (
          <blockquote className="mt-1.5 rounded-md border-l-2 border-border bg-muted/50 px-3 py-2">
            <p className="whitespace-pre-wrap text-sm text-foreground">{event.message}</p>
          </blockquote>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A `DetailRow` whose value is plain text that may be absent.
 *
 * The em-dash fallback is the convention across the request-detail surfaces:
 * a row that is missing data still occupies its slot, so the admin sees "we
 * have no value for this" rather than the row silently vanishing and the card
 * looking like it has fewer fields than the request actually has.
 *
 * Kept as a thin wrapper over the shared `DetailRow` rather than its own row
 * markup — the markup is what drifted last time.
 */
function TextDetailRow({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}): JSX.Element {
  const display = value != null && String(value).trim() !== "" ? String(value) : "—";
  return <DetailRow label={label}>{display}</DetailRow>;
}

/**
 * Payment state comes from the checkout's own status — never inferred from
 * the presence of a provider payment id, which exists from order creation.
 */
function paymentStatusLabelKey(status: string): string {
  if (status === "paid") return "platform.requestMoney.statusPaid";
  if (status === "cancelled") return "platform.requestMoney.statusCancelled";
  if (status === "failed") return "platform.requestMoney.statusFailed";
  return "platform.requestMoney.statusPending";
}

function paymentStatusVariant(status: string): "success" | "warning" | "danger" | "info" {
  if (status === "paid") return "success";
  if (status === "failed") return "danger";
  if (status === "cancelled") return "info";
  return "warning";
}

export function PlatformNotarialRequestDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget =
    typeof (location.state as { from?: unknown } | null)?.from === "string"
      ? ((location.state as { from: string }).from)
      : "/requests";
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requesterOpen, setRequesterOpen] = useState(false);

  const { data: request, isLoading, isError } = useQuery({
    queryKey: ["platform", "notarial-request", id],
    queryFn: () => getPlatformNotarialRequestDetail(id!),
    enabled: Boolean(id),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["platform", "notarial-request", id] });
    void queryClient.invalidateQueries({ queryKey: ["platform", "requests"] });
  };

  const [refundOpen, setRefundOpen] = useState(false);
  /**
   * The override is offered only AFTER a re-check has run and left the
   * checkout unpaid. Re-check reaches the same outcome when the money is real
   * and refuses when it is not, so putting it first makes the safe path the
   * default and the override the deliberate exception rather than a button of
   * equal standing (spec 2111a834, Decision 9).
   */
  const [recheckFailed, setRecheckFailed] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidReason, setMarkPaidReason] = useState("");

  /**
   * The channel this request is fulfilled on. Derived once here rather than
   * inline per call site: it decides the reassign endpoint, which professional
   * list the picker reads, and which activation field marks a professional
   * eligible, and those three must never disagree.
   */
  const channel = request?.platformRequestKind === "accountant" ? "accountant" : "notary";

  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignMode, setReassignMode] = useState<ReassignMode>("rebroadcast");
  const [reassignTarget, setReassignTarget] = useState("");
  const [reassignReason, setReassignReason] = useState("");

  /**
   * Candidates for a DIRECT assignment.
   *
   * Fetched only while the dialog is open and only in direct mode — the list
   * is unpaginated and irrelevant to every other action on this page.
   *
   * Filtered to activated professionals, which mirrors exactly what
   * assertProfessionalActivatedFor enforces server-side: the endpoint already
   * scopes to isNotary / isAccountant, so the activation status is the only
   * remaining condition. Offering a pending professional would be offering a
   * button that 400s.
   */
  const professionalsQuery = useQuery({
    queryKey: ["platform", "reassign-candidates", channel],
    queryFn: async () => {
      const project = (r: {
        id: string;
        email: string;
        displayName: string | null;
        activeAssignments: number;
        activeAssignmentLimit: number;
      }) => ({
        id: r.id,
        name: r.displayName?.trim() || r.email,
        load: r.activeAssignments,
        limit: r.activeAssignmentLimit,
        /**
         * At the cap already. Shown but NOT selectable: hiding them would
         * leave the admin wondering where someone went, while letting them
         * through would reproduce the 400 the picker exists to prevent. The
         * server still enforces it either way — this only moves the refusal
         * before the click.
         */
        atCap: r.activeAssignments >= r.activeAssignmentLimit,
      });
      if (channel === "accountant") {
        const rows = await listAccountants();
        return rows.filter((r) => r.accountantActivationStatus === "active").map(project);
      }
      const rows = await listNotaries();
      return rows.filter((r) => r.notaryActivationStatus === "active").map(project);
    },
    enabled: reassignOpen && reassignMode === "direct",
  });

  const closeReassign = (): void => {
    setReassignOpen(false);
    setReassignMode("rebroadcast");
    setReassignTarget("");
    setReassignReason("");
  };

  const reassignMutation = useMutation({
    mutationFn: () =>
      reassignRequest(channel, id!, {
        mode: reassignMode,
        toProfessionalUserId: reassignMode === "direct" ? reassignTarget : undefined,
        reason: reassignReason,
      }),
    onSuccess: () => {
      toast.success(
        reassignMode === "direct"
          ? t("platform.requestActions.reassignDoneDirect")
          : t("platform.requestActions.reassignDoneRebroadcast"),
      );
      closeReassign();
      invalidate();
    },
    /*
      The server's refusal is the only actionable part, and here it is
      especially so: "this professional already has 2 active requests", "that
      professional is not activated as a notary", "this request changed while
      you were looking at it". Each sends the admin somewhere different.
    */
    onError: (error) =>
      toast.error(serverMessage(error) ?? t("platform.requestActions.failed")),
  });

  /**
   * Pause and unpause are ONE control, not two buttons: the request is either
   * halted or it is not, and offering both at once asks the admin to work out
   * which applies.
   */
  const pauseMutation = useMutation({
    mutationFn: () =>
      request?.pausedAt ? unpauseRequest(channel, id!) : pauseRequest(channel, id!),
    onSuccess: () => invalidate(),
    // The server's refusal is the only actionable part: "dLocal is not
    // configured, so the payment cannot be refunded", "this checkout has no
    // provider payment id". A generic toast throws that away and leaves the
    // admin with nothing to go and fix.
    onError: (error) =>
      toast.error(serverMessage(error) ?? t("platform.requestActions.failed")),
  });

  /**
   * Reports what the provider says — it cannot manufacture a payment, which
   * is why it is offered freely while refund sits behind a confirmation.
   * The toast distinguishes "the state moved" from "the provider still says
   * the same thing", because those send the admin to different next steps.
   */
  const recheckMutation = useMutation({
    mutationFn: () => recheckRequestPayment(request!.money!.checkoutId),
    onSuccess: (result) => {
      toast.success(
        result.changed
          ? t("platform.requestActions.recheckChanged", { status: result.status })
          : t("platform.requestActions.recheckUnchanged", { status: result.status }),
      );
      // Only a re-check that ran and did NOT land on paid unlocks the override.
      setRecheckFailed(result.status !== "paid");
      invalidate();
    },
    // The server's refusal is the only actionable part: "dLocal is not
    // configured, so the payment cannot be refunded", "this checkout has no
    // provider payment id". A generic toast throws that away and leaves the
    // admin with nothing to go and fix.
    onError: (error) =>
      toast.error(serverMessage(error) ?? t("platform.requestActions.failed")),
  });

  const markPaidMutation = useMutation({
    mutationFn: () => markRequestPaymentPaid(request!.money!.checkoutId, markPaidReason),
    onSuccess: () => {
      toast.success(t("platform.requestActions.markPaidDone"));
      setMarkPaidOpen(false);
      setMarkPaidReason("");
      invalidate();
    },
    onError: (error) =>
      toast.error(serverMessage(error) ?? t("platform.requestActions.failed")),
  });

  const refundMutation = useMutation({
    mutationFn: () => refundRequestPayment(request!.money!.checkoutId),
    onSuccess: () => {
      toast.success(t("platform.requestActions.refundDone"));
      setRefundOpen(false);
      invalidate();
    },
    // The server's refusal is the only actionable part: "dLocal is not
    // configured, so the payment cannot be refunded", "this checkout has no
    // provider payment id". A generic toast throws that away and leaves the
    // admin with nothing to go and fix.
    onError: (error) =>
      toast.error(serverMessage(error) ?? t("platform.requestActions.failed")),
  });

  const deleteRequestMutation = useMutation({
    mutationFn: () => platformDeleteNotarialRequest(id!),
    onSuccess: () => {
      toast.success(t("platform.requests.deleteRequestSuccess"));
      setDeleteDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["platform", "requests"] });
      navigate("/requests");
    },
    onError: () => toast.error(t("platform.requests.deleteRequestError")),
  });

  useEffect(() => {
    if (isError) {
      toast.error(t("notaries.detail.requestLoadError"));
    }
  }, [isError, t]);

  if (!id) {
    navigate("/requests", { replace: true });
    return <></>;
  }

  if (isLoading || !request) {
    return <LoadingPage />;
  }

  const status = request.status as NotarialRequestStatus;
  const statusLabelKey = STATUS_LABELS[status] ?? request.status;
  const statusVariant = STATUS_VARIANTS[status] ?? "info";
  const hasDeliveredToConsumerEvent = request.events.some(
    (ev) => ev.eventType === "delivered_to_consumer",
  );
  /** The delivery address is for platform root only; support (root_admin) gets the overview without it. Nothing here is operational any more — the professional owns every fulfilment transition (spec f462e7d6). */
  const showFulfillmentSection = user?.platformRole === "root";

  // Whether the delivery block has any rows to list. Computed here rather than
  // inline so the `<dl>` is only emitted when it will hold something — an
  // empty DetailRows would still paint its divider rules.
  const hasDeliveryDetailRows =
    [request?.deliveryContactName, request?.deliveryContactPhone].some(Boolean) ||
    (request?.deliveryNotes?.trim() ?? "") !== "";
  /** Full platform operators (primary root or support / root_admin) can delete requests. */
  const canDeletePlatformRequest =
    user?.platformRole === "root" ||
    user?.platformRole === "root_admin" ||
    user?.role === "root" ||
    user?.role === "root_admin";

  const pageTitleKey =
    request.platformRequestKind === "accountant"
      ? "platform.requests.detailTitleAccountant"
      : "platform.requests.detailTitleNotary";

  // The delete confirmation is an overlay slot, not content: it paints
  // nothing in the flow and only ever opens from the header action.
  const refundDialog = (
    <ConfirmationDialog
      open={refundOpen}
      onOpenChange={setRefundOpen}
      title={t("platform.requestActions.refundConfirmTitle")}
      description={t("platform.requestActions.refundConfirmBody")}
      confirmText={t("platform.requestActions.refund")}
      cancelText={t("pages.common.cancel")}
      variant="destructive"
      isLoading={refundMutation.isPending}
      loadingText={t("platform.fulfillment.submitting")}
      onConfirm={async () => {
        await refundMutation.mutateAsync();
      }}
    />
  );

  const markPaidDialog = (
    <ConfirmationDialog
      open={markPaidOpen}
      onOpenChange={(open) => {
        setMarkPaidOpen(open);
        if (!open) setMarkPaidReason("");
      }}
      title={t("platform.requestActions.markPaidTitle")}
      description={t("platform.requestActions.markPaidBody")}
      confirmText={t("platform.requestActions.markPaid")}
      cancelText={t("pages.common.cancel")}
      variant="destructive"
      isLoading={markPaidMutation.isPending}
      loadingText={t("platform.fulfillment.submitting")}
      body={
        <Textarea
          value={markPaidReason}
          onChange={(e) => setMarkPaidReason(e.target.value)}
          placeholder={t("platform.requestActions.markPaidReason")}
          rows={3}
        />
      }
      onConfirm={async () => {
        // Guarded here rather than by disabling the button: the shared dialog
        // has no disabled-confirm prop, and adding one for a single caller
        // would change a component every destructive flow depends on. The
        // server refuses an empty reason regardless — this just stops the
        // admin discovering that by failing.
        if (markPaidReason.trim().length < 3) {
          toast.error(t("platform.requestActions.markPaidReasonRequired"));
          return;
        }
        await markPaidMutation.mutateAsync();
      }}
    />
  );

  const reassignCandidates = professionalsQuery.data ?? [];

  const reassignDialog = (
    <ConfirmationDialog
      open={reassignOpen}
      onOpenChange={(open) => {
        if (!open) closeReassign();
        else setReassignOpen(true);
      }}
      title={t("platform.requestActions.reassignTitle")}
      description={t("platform.requestActions.reassignBody")}
      confirmText={t("platform.requestActions.reassign")}
      cancelText={t("pages.common.cancel")}
      variant="destructive"
      isLoading={reassignMutation.isPending}
      loadingText={t("platform.fulfillment.submitting")}
      body={
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("platform.requestActions.reassignModeLabel")}</Label>
            {/*
              Mode is a RADIO, not a second button on the card. Returning a
              request to the pool and handing it to a named person are one
              decision with two answers, and the reason and confirmation are
              identical either way — two separate controls would ask the admin
              to notice that for themselves.
            */}
            <RadioGroup
              value={reassignMode}
              onValueChange={(value) => {
                setReassignMode(value as ReassignMode);
                // The target only means something in direct mode; carrying a
                // stale one across a switch would submit a professional the
                // admin had stopped looking at.
                setReassignTarget("");
              }}
              className="gap-2"
            >
              <div className="flex items-start gap-2">
                <RadioGroupItem value="rebroadcast" id="reassign-rebroadcast" className="mt-1" />
                <Label htmlFor="reassign-rebroadcast" className="font-normal leading-snug">
                  {t("platform.requestActions.reassignModeRebroadcast")}
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="direct" id="reassign-direct" className="mt-1" />
                <Label htmlFor="reassign-direct" className="font-normal leading-snug">
                  {t("platform.requestActions.reassignModeDirect")}
                </Label>
              </div>
            </RadioGroup>
          </div>

          {reassignMode === "direct" ? (
            <div className="space-y-2">
              <Label htmlFor="reassign-target">
                {t("platform.requestActions.reassignTargetLabel")}
              </Label>
              {professionalsQuery.isLoading ? (
                <p className="text-xs text-muted-foreground">
                  {t("platform.requestActions.reassignTargetLoading")}
                </p>
              ) : reassignCandidates.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("platform.requestActions.reassignTargetEmpty")}
                </p>
              ) : (
                <Select value={reassignTarget} onValueChange={setReassignTarget}>
                  <SelectTrigger id="reassign-target">
                    <SelectValue
                      placeholder={t("platform.requestActions.reassignTargetPlaceholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {reassignCandidates
                      // The professional already holding it is not a move.
                      // The server refuses it too; this stops the admin
                      // discovering that by failing.
                      .filter((c) => c.id !== request?.acceptedByUserId)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id} disabled={c.atCap}>
                          {c.atCap
                            ? t("platform.requestActions.reassignTargetAtCap", {
                                name: c.name,
                                load: c.load,
                                limit: c.limit,
                              })
                            : t("platform.requestActions.reassignTargetOption", {
                                name: c.name,
                                load: c.load,
                                limit: c.limit,
                              })}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="reassign-reason">
              {t("platform.requestActions.reassignReason")}
            </Label>
            <Textarea
              id="reassign-reason"
              value={reassignReason}
              onChange={(e) => setReassignReason(e.target.value)}
              placeholder={t("platform.requestActions.reassignReason")}
              rows={3}
            />
          </div>
        </div>
      }
      onConfirm={async () => {
        // Guarded here rather than by disabling the button: the shared dialog
        // has no disabled-confirm prop, and adding one for a single caller
        // would change a component every destructive flow depends on.
        if (reassignMode === "direct" && !reassignTarget) {
          toast.error(t("platform.requestActions.reassignTargetRequired"));
          return;
        }
        if (reassignReason.trim().length < 3) {
          toast.error(t("platform.requestActions.reassignReasonRequired"));
          return;
        }
        await reassignMutation.mutateAsync();
      }}
    />
  );

  const deleteDialog = (
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t("platform.requests.deleteRequestConfirmTitle")}
        description={t("platform.requests.deleteRequestConfirmDescription")}
        confirmText={t("platform.requests.deleteRequest")}
        cancelText={t("pages.common.cancel")}
        variant="destructive"
        isLoading={deleteRequestMutation.isPending}
        loadingText={t("platform.fulfillment.submitting")}
        onConfirm={async () => {
          await deleteRequestMutation.mutateAsync();
        }}
      />
  );

  return (
    <RequestDetailLayout
      title={t(pageTitleKey)}
      description={t("notary.requestDetail.idLabel", { id: id.slice(0, 8) })}
      onBack={() => navigate(backTarget)}
        actions={
          canDeletePlatformRequest ? (
            <Button
              type="button"
              variant="destructive"
              className="transition-colors duration-150"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden />
              {t("platform.requests.deleteRequest")}
            </Button>
          ) : null
        }
      overlays={
        <>
          {deleteDialog}
          {refundDialog}
          {markPaidDialog}
          {reassignDialog}
          <UserDetailDialog
            userId={request.requestedByUserId}
            open={requesterOpen}
            onOpenChange={setRequesterOpen}
          />
        </>
      }
    >
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-foreground">
            {t("notary.requestDetail.detailsTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DetailRows>
            <DetailRow label={t("notaries.columnStatus")}>
              <StatusBadge variant={statusVariant}>{t(statusLabelKey)}</StatusBadge>
            </DetailRow>
            {/* A link, not text: an admin deciding anything about this request
                routinely needs to know who asked and where they receive
                things, and there is no other admin surface for a consumer
                (spec 218021de). */}
            <DetailRow label={t("notary.requestDetail.requester")}>
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setRequesterOpen(true)}
              >
                {request.requesterName ?? "—"}
              </Button>
            </DetailRow>
            <TextDetailRow label={t("notaries.columnEmail")} value={request.requesterEmail} />
            {/*
              Who is actually doing the work. Deliberately OUTSIDE the
              fulfillment section: the professional's name previously reached
              this page only inside "Retiro del profesional", which renders
              for PHYSICAL fulfilment, so a digital-only request never named
              them — while the list one click back shows an "Asignado a"
              column. Same i18n key as that column on purpose, so the two
              surfaces cannot drift apart on the word.

              Rendered even when unassigned: TextDetailRow shows "—", and
              "nobody has taken this yet" is information an admin monitoring
              a request needs, not an empty row to hide.
            */}
            <TextDetailRow
              label={t("platform.requests.assignee")}
              value={request.assignedProfessionalName}
            />
            {!showFulfillmentSection ? (
              <TextDetailRow
                label={t("notaries.detail.identityDocument")}
                value={
                  request.requesterDocumentType && request.requesterDocumentNumber
                    ? `${request.requesterDocumentType} ${request.requesterDocumentNumber}`
                    : null
                }
              />
            ) : null}
            <TextDetailRow
              label={t("notaries.detail.colAccepted")}
              value={
                request.acceptedAt != null ? formatDisplayDate(request.acceptedAt) : null
              }
            />
            <TextDetailRow
              label={t("notaries.detail.colUpdated")}
              value={formatDisplayDate(request.updatedAt)}
            />
            {showFulfillmentSection && request.fulfillmentInternalNotes ? (
              <TextDetailRow
                label={t("platform.fulfillment.internalNotes")}
                value={request.fulfillmentInternalNotes}
              />
            ) : null}
          </DetailRows>
        </CardContent>
      </Card>

      {/*
        What an admin can DO from here.

        Pause/unpause and the payment actions already existed as endpoints and
        lived on other pages, so noticing a problem here meant navigating away
        to act on it — which is what made this page fail as a monitoring
        surface rather than merely an incomplete one.

        Re-check is first and refund is destructive-confirmed, because
        re-checking is the action that fixes the common real failure: a
        consumer who paid and never returned to the site, leaving the row
        pending because the webhook never arrived either.
      */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-foreground">
            {t("platform.requestActions.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {request.pausedAt ? (
            <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
              <AlertDescription className="text-muted-foreground">
                {t("platform.requestActions.pausedNotice")}
                {request.pauseReason ? ` — ${request.pauseReason}` : ""}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            {/*
              Hidden once the request is finished. Gated on the SERVER's
              pausable flag, never on a status list restated here — the same
              reasoning as `reassignable`. The endpoint refuses too
              (assertRequestPausable); this only stops the admin discovering
              that by clicking.

              A paused request that then finished still shows the control, so
              there is always a way to lift a pause that is already stamped.
            */}
            {request.pausable || request.pausedAt ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={pauseMutation.isPending}
                onClick={() => pauseMutation.mutate()}
              >
                {request.pausedAt
                  ? t("platform.requestActions.unpause")
                  : t("platform.requestActions.pause")}
              </Button>
            ) : null}
            {/*
              Gated on the SERVER's reassignable flag, never on a status list
              restated here. It goes false once a payout line may exist, and a
              copy of that list in the portal would drift from the one
              RequestReassignService enforces — leaving a button that 400s.

              Hidden rather than disabled: a disabled control invites the
              admin to hunt for what would re-enable it, and nothing on this
              page will. The hint below says why it is not there.
            */}
            {request.reassignable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={reassignMutation.isPending}
                onClick={() => setReassignOpen(true)}
              >
                {t("platform.requestActions.reassign")}
              </Button>
            ) : null}
            {request.money ? (
              <>
                {/*
                  Re-check exists to resolve a payment the processor never
                  confirmed. Once the row says paid there is nothing left to
                  ask it, so the button is noise rather than an option.
                  Derived from money.status, which is already on the DTO — no
                  status list is duplicated here, so this needs no server flag.
                */}
                {request.money.status !== "paid" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={recheckMutation.isPending}
                    onClick={() => recheckMutation.mutate()}
                  >
                    {t("platform.requestActions.recheck")}
                  </Button>
                ) : null}
                {request.money.status === "paid" ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={refundMutation.isPending}
                    onClick={() => setRefundOpen(true)}
                  >
                    {t("platform.requestActions.refund")}
                  </Button>
                ) : null}
              </>
            ) : null}
          </div>
          {request.money && request.money.status !== "paid" ? (
            <p className="text-xs text-muted-foreground">
              {t("platform.requestActions.recheckHint")}
            </p>
          ) : null}
          {request.reassignable ? (
            <p className="text-xs text-muted-foreground">
              {t("platform.requestActions.reassignHint")}
            </p>
          ) : null}
          {/*
            The override. Rendered apart from the actions above and only once
            a re-check has run and left the checkout unpaid — it is the one
            control here that writes a paid record with no provider behind it,
            and it should never sit among the ordinary buttons as if it were
            one of them.
          */}
          {request.money && request.money.status !== "paid" && recheckFailed ? (
            <div className="rounded-md border border-dashed border-amber-500/60 bg-amber-500/5 p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={markPaidMutation.isPending}
                onClick={() => setMarkPaidOpen(true)}
              >
                {t("platform.requestActions.markPaid")}
              </Button>
              <p className="mt-2 text-xs text-muted-foreground">
                {t("platform.requestActions.markPaidHint")}
              </p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/*
        Where this request came from, when it came from an RFQ.

        Branched on rfqOrigin PRESENCE, never on quotes.length: an empty
        "Presupuestos recibidos" on a catalog purchase would tell the admin
        nobody quoted, when quoting never applied to it at all. Absence and
        emptiness say different things (spec 2111a834, control C-4).

        The bid rows come from the same mapper the RFQ page uses, so the two
        surfaces cannot disagree about a bid (C-5). Depth — transcript, legs,
        RFQ timeline — stays one click away rather than doubling this page.
      */}
      {request.rfqOrigin ? (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-3">
            <CardTitle className="text-base text-foreground">
              {t("platform.requestRfq.title")}
            </CardTitle>
            <Link
              to={`/platform/quote-requests/${request.rfqOrigin.quoteRequestId}`}
              className="text-xs text-primary underline underline-offset-4 hover:no-underline"
            >
              {t("platform.requestRfq.viewFull")}
            </Link>
          </CardHeader>
          <CardContent className="space-y-3">
            {request.rfqOrigin.professionalEarningsMinor != null ? (
              <DetailRows>
                <TextDetailRow
                  label={t("platform.requestRfq.earnings")}
                  value={formatUyMinor(request.rfqOrigin.professionalEarningsMinor)}
                />
              </DetailRows>
            ) : null}
            {request.rfqOrigin.quotes.length === 0 ? (
              <EmptyState
                title={t("platform.requestRfq.noQuotesTitle")}
                message={t("platform.requestRfq.noQuotes")}
              />
            ) : (
              <ul className="divide-y divide-border">
                {request.rfqOrigin.quotes.map((quote) => {
                  const isAwarded = quote.id === request.rfqOrigin?.awardedQuoteId;
                  return (
                    <li
                      key={quote.id}
                      className="flex flex-wrap items-center justify-between gap-2 py-2.5"
                    >
                      <span className="flex items-center gap-2 text-sm text-foreground">
                        {quote.professionalDisplayName ?? quote.professionalUserId}
                        {isAwarded ? (
                          <StatusBadge variant="success">
                            {t("platform.requestRfq.awarded")}
                          </StatusBadge>
                        ) : null}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {formatUyMinor(quote.priceMinor, quote.currency)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/*
        What this request was actually charged.

        Rendered only when a checkout exists: "never charged" and "charged
        nothing" are different facts, and a zeroed card asserts the wrong one
        (spec 2111a834, control C-2). Every figure is read from the checkout
        row — never recomputed, because the rate tables are admin-editable and
        re-deriving would price a historical charge at today's rates.
      */}
      {request.money ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {t("platform.requestMoney.title")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRows>
              <DetailRow label={t("platform.requestMoney.status")}>
                <StatusBadge variant={paymentStatusVariant(request.money.status)}>
                  {t(paymentStatusLabelKey(request.money.status))}
                </StatusBadge>
              </DetailRow>
              {request.money.paidAt ? (
                <TextDetailRow
                  label={t("platform.requestMoney.paidAt")}
                  value={formatDisplayDate(request.money.paidAt)}
                />
              ) : null}
              <TextDetailRow
                label={t("platform.requestMoney.total")}
                value={formatUyMinor(request.money.amountMinor, request.money.currency)}
              />
              <TextDetailRow
                label={t("platform.requestMoney.fee")}
                value={
                  request.money.consumerServiceFeeMinor != null
                    ? formatUyMinor(request.money.consumerServiceFeeMinor, request.money.currency)
                    : null
                }
              />
              {/* Echo of the payout figure. Pagos a profesionales stays the
                  source of truth, so the hint says so rather than letting an
                  admin reconcile against this page. */}
              <DetailRow label={t("platform.requestMoney.earnings")}>
                <span className="text-sm text-foreground">
                  {request.money.professionalEarningsMinor != null
                    ? formatUyMinor(
                        request.money.professionalEarningsMinor,
                        request.money.currency,
                      )
                    : "—"}
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {t("platform.requestMoney.earningsHint")}
                </span>
              </DetailRow>
              <TextDetailRow
                label={t("platform.requestMoney.provider")}
                value={request.money.provider}
              />
              {/* NOT evidence of payment — the id is assigned when the order
                  is created, so a never-paid checkout has one too. Labelled
                  as a reference, with state read from `status` above (C-7). */}
              {request.money.providerPaymentId ? (
                <DetailRow label={t("platform.requestMoney.reference")}>
                  <span className="break-all font-mono text-xs text-foreground">
                    {request.money.providerPaymentId}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {t("platform.requestMoney.referenceHint")}
                  </span>
                </DetailRow>
              ) : null}
            </DetailRows>
          </CardContent>
        </Card>
      ) : null}

      {/* READ-ONLY NOW (spec f462e7d6). This card used to be where a Gestionalo
          operator drove the middle of the chain: mark the instrument received
          from the professional, ask the consumer for an address, mark it
          shipped, close it. All five routes are deleted server-side, so every
          button here was a 404 waiting to be clicked.

          What survives is the part support actually reads: WHERE the gestión
          is going, and whether the professional has said it arrived. The
          pickup block is gone outright — no pickup address is collected from
          anyone any more. */}
      {showFulfillmentSection ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {t("platform.fulfillment.addressesCardTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3 rounded-xl border border-border bg-muted/30 p-5 dark:bg-muted/20">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {t("platform.fulfillment.deliveryAddressSectionTitle")}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("platform.fulfillment.professionalDeliversHint")}
                </p>
              </div>
              <p className="whitespace-pre-wrap text-sm text-foreground">
                {request.deliveryAddressSnapshot?.trim()
                  ? request.deliveryAddressSnapshot
                  : t("platform.fulfillment.addressNotSetYet")}
              </p>
              {hasDeliveryDetailRows ? (
                <DetailRows>
                  {[request.deliveryContactName, request.deliveryContactPhone].some(Boolean) ? (
                    <TextDetailRow
                      label={t("platform.fulfillment.deliveryContact")}
                      value={
                        [request.deliveryContactName, request.deliveryContactPhone]
                          .filter(Boolean)
                          .join(" · ") || null
                      }
                    />
                  ) : null}
                  {request.deliveryNotes?.trim() ? (
                    <TextDetailRow
                      label={t("platform.fulfillment.deliveryNotes")}
                      value={request.deliveryNotes}
                    />
                  ) : null}
                </DetailRows>
              ) : null}
              {hasDeliveredToConsumerEvent ? (
                <p className="flex items-center gap-2 border-t border-border pt-4 text-sm text-muted-foreground">
                  <CheckCircle2
                    className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400"
                    aria-hidden
                  />
                  {t("platform.fulfillment.deliveredConfirmed")}
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {!showFulfillmentSection && request.documents.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {t("notaries.detail.uploadedFiles")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {request.documents.map((doc) => (
                <li key={doc.id} className="flex items-center gap-2 text-sm">
                  <AuthenticatedBlobDocumentLink
                    fetchBlob={() => fetchPlatformNotarialRequestDocumentBlob(id, doc.id)}
                    title={doc.originalFilename}
                    className="inline-flex items-center gap-1 hover:underline"
                  >
                    <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    {doc.originalFilename}
                  </AuthenticatedBlobDocumentLink>
                  <span className="text-muted-foreground">({doc.documentType})</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {!showFulfillmentSection ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-foreground">
              {t("notary.requestDetail.historyTitle")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {request.events.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("notary.requestDetail.historyEmpty")}
              </p>
            ) : (
              <div>
                {request.events.map((ev, index) => (
                  <TimelineEventReadOnly
                    key={ev.id}
                    event={ev}
                    isLast={index === request.events.length - 1}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}
    </RequestDetailLayout>
  );
}
